<script setup lang="ts">
import { Link, usePage } from '@inertiajs/vue3';
import { ArrowLeft, Plus, RotateCcw } from '@lucide/vue';
import { computed, onMounted, ref, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';

import { Button } from '@/components/ui/button/index';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card/index';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip/index';

import { useAutoGenerate } from '../composables/useAutoGenerate';
import { useCreatorContext } from '../composables/useCrudContext';
import { useCrudForm } from '../composables/useCrudForm';
import { useFieldVisibility } from '../composables/useFieldVisibility';
import { useTranslatable } from '../composables/useTranslatable';
import type { CreatorPageProps } from '../types';
import { index } from '../wayfinder/routes';

import BlockConfigComponent from './ConfigBuilder/BlockConfig.vue';
import FieldConfigComponent from './ConfigBuilder/FieldConfig.vue';
import CrudTagsEditor from './CrudTagsEditor.vue';

defineProps<Partial<CreatorPageProps>>();
const contextProps = useCreatorContext();

const { t } = useI18n();
const page = usePage();

const creator = useCrudForm(contextProps);
const { activeModel, activeConfig } = creator;
const { trField, trBlock, te, t: trT } = useTranslatable(activeConfig);

const isMounted = ref(false);
onMounted(() => {
    isMounted.value = true;
});

/** Titles shortcut */
const titles = computed(() => {
    const prefix = activeConfig.value?.translationPrefix;
    const fallbackSingular = activeConfig.value?.titles.singular ?? '';
    const fallbackPlural = activeConfig.value?.titles.plural ?? '';
    
    if (!prefix) return { singular: fallbackSingular, plural: fallbackPlural };
    
    return {
        singular: te(`models.${prefix}.singular`) ? trT(`models.${prefix}.singular`) : fallbackSingular,
        plural: te(`models.${prefix}.plural`) ? trT(`models.${prefix}.plural`) : fallbackPlural,
    };
});

/** Back route - preserves parent context */
const backRoute = computed(() => {
    const baseUrl = index({ model: activeModel.value }).url;
    if (contextProps.parentContext) {
        const params = new URLSearchParams({
            [contextProps.parentContext.foreignKey]: contextProps.parentContext.id,
        });
        return `${baseUrl}?${params.toString()}`;
    }
    return baseUrl;
});

/** Get parent display title */
const parentDisplayTitle = computed(() => {
    if (!contextProps.parentContext?.item) return contextProps.parentContext?.id ?? '';
    const titleField = contextProps.parentContext.config?.editor?.titleField || 'id';
    return contextProps.parentContext.item[titleField] ?? contextProps.parentContext.id;
});

const canManageTags = computed(() => {
    const crud = (page.props as any)?.crud;
    return crud?.active?.permissions?.update === true;
});

/** Group fields by their section (if any), filtering out parent field */
const fields = computed(() => {
    let result = creator.getFields().filter((f) => f.key !== '__crud_tags');
    // Filter out the parent foreign key field - we show it as readonly text instead
    if (contextProps.parentContext) {
        result = result.filter((f) => f.key !== contextProps.parentContext?.foreignKey);
    }
    return result;
});

// Field visibility for direct fields (non-block mode)
const formRef = computed(() => creator.form as Record<string, unknown>);
const { isFieldVisible } = useFieldVisibility(fields, formRef);

// Auto-generate support
const allFields = computed(() => creator.getFields());
const autoGen = useAutoGenerate(allFields, formRef);

/**
 * Watch form data changes and auto-update fields that have auto-generate config.
 * Uses watchEffect to reactively re-compute whenever source fields change.
 */
watchEffect(() => {
    for (const field of autoGen.autoGenerateFields.value) {
        if (autoGen.isManuallyEdited(field.key)) continue;

        const generated = autoGen.getAutoGeneratedValue(field.key);
        if (generated !== null) {
            const current = creator.getFieldValue(field.key);
            if (current !== generated) {
                creator.setFieldValue(field.key, generated);
            }
        }
    }
});

/** Get field value supporting dot notation (e.g., 'data.start_date') */
function getFieldValue(key: string): unknown {
    return creator.getFieldValue(key);
}

/**
 * Coerce truthiness for `metadata.implies_true` propagation. Mirrors the
 * helper in BlockConfig/TabConfig so the creator's direct-field path picks up
 * the same implication behavior.
 */
function isFieldTruthy(value: unknown, trueValue: unknown): boolean {
    if (trueValue !== undefined && trueValue !== null) {
        return value === trueValue;
    }
    return value === true || value === 1 || value === '1' || value === 'true';
}

/** Handle field update supporting dot notation */
function handleFieldUpdate(key: string, value: unknown): void {
    creator.setFieldValue(key, value);

    const field = fields.value.find((f) => f.key === key);
    const implies = (field?.metadata as Record<string, unknown> | undefined)?.implies_true;
    if (Array.isArray(implies) && isFieldTruthy(value, field?.trueValue)) {
        for (const impliedKey of implies) {
            if (typeof impliedKey !== 'string') continue;
            const impliedField = fields.value.find((f) => f.key === impliedKey);
            const impliedTrue = impliedField?.trueValue ?? true;
            creator.setFieldValue(impliedKey, impliedTrue);
        }
    }
}

/** Handle manual edit of an auto-generated field */
function handleManualEdit(key: string): void {
    autoGen.markManualEdit(key);
}

/** Reset auto-generation for a field */
function handleResetAutoGenerate(key: string): void {
    autoGen.resetAutoGenerate(key);
}
</script>

<template>
    <div v-if="activeConfig" class="-mb-6 space-y-6">
        <!-- Header -->
        <Teleport v-if="isMounted" to="#header-portal">
            <div class="flex items-center gap-2 overflow-hidden px-2">
                <Link :href="backRoute">
                    <Button variant="ghost" size="icon-sm">
                        <ArrowLeft class="size-4" />
                    </Button>
                </Link>
                <div class="min-w-0">
                    <h1 class="truncate text-lg font-bold tracking-tight">
                        {{ t('crud.create_title', { singular: titles.singular }) }}
                    </h1>
                    <p class="text-muted-foreground truncate text-xs">
                        <template v-if="contextProps.parentContext">
                            {{ contextProps.parentContext.config?.titles.singular }}:
                            {{ parentDisplayTitle }}
                        </template>
                        <template v-else>
                            {{
                                t('crud.create_description', {
                                    singular: titles.singular.toLowerCase(),
                                })
                            }}
                        </template>
                    </p>
                </div>
            </div>
        </Teleport>

        <!-- Form -->
        <form id="crud-creator-form" @submit.prevent="creator.submit">
            <template v-if="activeConfig.creator.blocks?.length">
                <div class="@container/creator">
                    <!--
                        CSS multi-column layout flows blocks into N columns
                        without forcing equal-height rows — short cards stack
                        naturally beneath taller siblings (masonry-style).
                        `break-inside-avoid` keeps each card whole.
                    -->
                    <div class="@[768px]/creator:columns-2 columns-1 gap-6 [&>*]:mb-6 [&>*]:break-inside-avoid [&>*:last-child]:mb-0">
                        <BlockConfigComponent
                            v-for="block in activeConfig.creator.blocks"
                            :key="block.key"
                            :block="block"
                            :form="creator.form"
                            :errors="creator.form.errors"
                            :tr-field="trField"
                            :tr-block="trBlock"
                            :translation-prefix="activeConfig?.translationPrefix"
                            :model="activeModel"
                            :auto-generate="autoGen"
                            @update:field="(key, value) => ((creator.form as any)[key] = value)"
                        />
                    </div>
                </div>
            </template>

            <template v-else>
                <Card class="@container/creator-card">
                    <CardHeader>
                        <CardTitle>{{ t('crud.details', { singular: titles.singular }) }}</CardTitle>
                        <CardDescription>
                            {{ t('crud.fill_info') }}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div class="@[400px]/creator-card:grid-cols-2 @[700px]/creator-card:grid-cols-3 grid grid-cols-1 gap-6">
                            <FieldConfigComponent
                                v-for="field in fields"
                                v-show="isFieldVisible(field)"
                                :key="field.key"
                                :field="field"
                                :model-value="getFieldValue(field.key)"
                                :error="creator.form.errors[field.key]"
                                :tr-field="trField"
                                :translation-prefix="activeConfig?.translationPrefix"
                                :model="activeModel"
                                :is-auto-generating="autoGen.hasAutoGenerate(field.key)"
                                :is-manually-edited="autoGen.isManuallyEdited(field.key)"
                                @update:model-value="(value: any) => handleFieldUpdate(field.key, value)"
                                @manual-edit="handleManualEdit"
                                @reset-auto-generate="handleResetAutoGenerate"
                            />
                        </div>
                    </CardContent>
                </Card>
            </template>

            <CrudTagsEditor
                v-if="activeModel && canManageTags"
                class="mt-4"
                :model="activeModel"
                :model-value="(creator.getFieldValue('__crud_tags') as string[] | null | undefined)"
                :allow-management="canManageTags"
                @update:model-value="(value) => creator.setFieldValue('__crud_tags', value)"
            />

            <Teleport v-if="isMounted" to="#footer-portal">
                <div class="flex flex-1 items-center justify-end gap-2">
                    <Link :href="backRoute">
                        <Button type="button" variant="outline">
                            {{ t('crud.cancel') }}
                        </Button>
                    </Link>
                    <Button v-show="creator.isDirty.value" type="button" variant="outline" @click="creator.reset">
                        <RotateCcw class="mr-2 size-4" />
                        {{ t('crud.reset') }}
                    </Button>
                    <TooltipProvider :delay-duration="300">
                        <Tooltip>
                            <TooltipTrigger as-child>
                                <Button
                                    type="submit"
                                    form="crud-creator-form"
                                    size="icon-sm"
                                    :disabled="creator.isProcessing.value"
                                    :aria-label="t('crud.create', { singular: titles.singular })"
                                >
                                    <Plus class="size-4" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                                {{ t('crud.create', { singular: titles.singular }) }}
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>
            </Teleport>
        </form>
    </div>
</template>
